import { Link, useNavigate } from "react-router-dom";
import { useTranslations } from "@/lib/useTranslations";
import { useCallback, useEffect, useState } from "react";
import { useRefresh } from "@/hooks/useRefresh";

import {
  addArticleSuggestedEvent,
  addArticleSuggestedPlace,
  removeArticleSuggestedEvent,
  removeArticleSuggestedPlace,
  searchArticleSuggestionEvents,
  searchArticleSuggestionPlaces,
} from "@/api/article-suggestions";
import { Button } from "@/components/ui/Button";
import { ArticleSuggestionSearchField } from "@/components/ui/admin/articles/ArticleSuggestionSearchField";
import { defaultLocale } from "@/i18n/config";
import { formatEventCalendarDate } from "@/lib/event-dates";
import type {
  ArticleSuggestionSearchEvent,
  ArticleSuggestionSearchPlace,
  ArticleSuggestedEvent,
  ArticleSuggestedPlace,
  ArticleSuggestions,
  ContentStatus,
} from "@/core/services/types/article.types";
import { getEventSharePath } from "@/core/services/types/event.types";
import { getPlaceSharePath } from "@/core/services/types/place.types";
import type { Enums } from "@/types/database.types";

type EventCategory = Enums<"event_category_enum">;

interface ArticleSuggestionsPanelProps {
  articleId: string;
  suggestions: ArticleSuggestions;
}

function formatDateRange(startDate: string | null, endDate: string | null): string {
  if (!startDate && !endDate) return "—";

  const start = startDate ? formatEventCalendarDate(startDate, defaultLocale) : null;
  const end = endDate ? formatEventCalendarDate(endDate, defaultLocale) : null;

  if (start && end) {
    return start === end ? start : `${start} – ${end}`;
  }

  return start ?? end ?? "—";
}

function StatusBadge({ status }: { status: ContentStatus }) {
  const t = useTranslations("articles.library.statuses");

  const styles: Record<ContentStatus, string> = {
    SIGNAL: "bg-violet-50 text-violet-700",
    DRAFT: "bg-neutral-100 text-neutral-600",
    PENDING_REVIEW: "bg-amber-50 text-amber-700",
    SCHEDULED: "bg-amber-50 text-amber-800",
    PUBLISHED: "bg-emerald-50 text-emerald-700",
    ARCHIVED: "bg-neutral-100 text-neutral-500",
  };

  return (
    <span
      className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${styles[status]}`}
    >
      {t(status)}
    </span>
  );
}

function EventCategoryLabel({
  category,
}: {
  category: EventCategory | null;
}) {
  const t = useTranslations("places.edit.events");

  if (!category) {
    return (
      <span className="text-neutral-400">{t("categoryUnassigned")}</span>
    );
  }

  return <span>{t(`eventCategories.${category}`)}</span>;
}

export function ArticleSuggestionsPanel({
  articleId,
  suggestions,
}: ArticleSuggestionsPanelProps) {
  const t = useTranslations("articles.edit.suggestions");
  const navigate = useNavigate();
  const refresh = useRefresh();
  const [places, setPlaces] = useState(suggestions.places);
  const [events, setEvents] = useState(suggestions.events);
  const [mutationError, setMutationError] = useState<string | null>(null);

  useEffect(() => {
    setPlaces(suggestions.places);
    setEvents(suggestions.events);
  }, [suggestions]);

  const excludePlaceIds = places.map((place) => place.placeId);
  const excludeEventIds = events.map((event) => event.eventId);

  const searchPlaces = useCallback(
    async (query: string) => {
      const result = await searchArticleSuggestionPlaces(query, excludePlaceIds);
      if (!result.success) {
        return { success: false as const, error: result.error, items: [] };
      }
      return { success: true as const, items: result.places };
    },
    [excludePlaceIds],
  );

  const searchEvents = useCallback(
    async (query: string) => {
      const result = await searchArticleSuggestionEvents(query, excludeEventIds);
      if (!result.success) {
        return { success: false as const, error: result.error, items: [] };
      }
      return { success: true as const, items: result.events };
    },
    [excludeEventIds],
  );

  async function handleAddPlace(item: ArticleSuggestionSearchPlace) {
    setMutationError(null);
    const result = await addArticleSuggestedPlace(articleId, item.id);
    if (!result.success) {
      return { success: false, error: result.error };
    }
    setPlaces(result.data.places);
    setEvents(result.data.events);
    refresh();
    return { success: true };
  }

  async function handleRemovePlace(placeId: string) {
    setMutationError(null);
    const result = await removeArticleSuggestedPlace(articleId, placeId);
    if (!result.success) {
      setMutationError(result.error);
      return;
    }
    setPlaces(result.data.places);
    setEvents(result.data.events);
    refresh();
  }

  async function handleAddEvent(item: ArticleSuggestionSearchEvent) {
    setMutationError(null);
    const result = await addArticleSuggestedEvent(articleId, item.id);
    if (!result.success) {
      return { success: false, error: result.error };
    }
    setPlaces(result.data.places);
    setEvents(result.data.events);
    refresh();
    return { success: true };
  }

  async function handleRemoveEvent(eventId: string) {
    setMutationError(null);
    const result = await removeArticleSuggestedEvent(articleId, eventId);
    if (!result.success) {
      setMutationError(result.error);
      return;
    }
    setPlaces(result.data.places);
    setEvents(result.data.events);
    refresh();
  }

  return (
    <div className="space-y-8">
      {mutationError ? (
        <p role="alert" className="wireframe-border px-3 py-2 text-sm text-whart-urgent">
          {mutationError}
        </p>
      ) : null}

      <section className="wireframe-border bg-whart-bg">
        <div className="border-b border-black/10 px-4 py-3">
          <h2 className="text-sm font-medium text-whart-text">{t("places.title")}</h2>
          <p className="mt-1 text-sm text-neutral-600">
            {t("places.count", { count: places.length })}
          </p>
        </div>

        <div className="space-y-4 px-4 py-4">
          <ArticleSuggestionSearchField
            label={t("places.searchLabel")}
            placeholder={t("places.searchPlaceholder")}
            searchingLabel={t("searching")}
            selectHintLabel={t("selectHint")}
            noResultsLabel={t("noResults")}
            disabled={false}
            onSearch={searchPlaces}
            onSelect={handleAddPlace}
            getItemId={(item) => item.id}
            renderPrimary={(item) => item.name}
            renderSecondary={(item) => item.city ?? null}
          />

          {places.length === 0 ? (
            <p className="text-sm text-neutral-500">{t("places.empty")}</p>
          ) : (
            <SuggestedPlacesTable
              places={places}
              onRemove={handleRemovePlace}
            />
          )}
        </div>
      </section>

      <section className="wireframe-border bg-whart-bg">
        <div className="border-b border-black/10 px-4 py-3">
          <h2 className="text-sm font-medium text-whart-text">{t("events.title")}</h2>
          <p className="mt-1 text-sm text-neutral-600">
            {t("events.count", { count: events.length })}
          </p>
        </div>

        <div className="space-y-4 px-4 py-4">
          <ArticleSuggestionSearchField
            label={t("events.searchLabel")}
            placeholder={t("events.searchPlaceholder")}
            searchingLabel={t("searching")}
            selectHintLabel={t("selectHint")}
            noResultsLabel={t("noResults")}
            disabled={false}
            onSearch={searchEvents}
            onSelect={handleAddEvent}
            getItemId={(item) => item.id}
            renderPrimary={(item) => item.title}
            renderSecondary={(item) => formatDateRange(item.start_date, item.end_date)}
          />

          {events.length === 0 ? (
            <p className="text-sm text-neutral-500">{t("events.empty")}</p>
          ) : (
            <SuggestedEventsTable
              events={events}
              onRemove={handleRemoveEvent}
            />
          )}
        </div>
      </section>
    </div>
  );
}

function SuggestedPlacesTable({
  places,
  onRemove,
}: {
  places: ArticleSuggestedPlace[];
  onRemove: (placeId: string) => void;
}) {
  const t = useTranslations("articles.edit.suggestions.places");
  const navigate = useNavigate();

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[36rem] table-fixed text-left text-sm">
        <colgroup>
          <col />
          <col className="w-40" />
          <col className="w-32" />
          <col className="w-28" />
        </colgroup>
        <thead>
          <tr className="wireframe-border-b bg-neutral-50 text-xs font-medium uppercase tracking-wider text-neutral-500">
            <th className="px-3 py-2">{t("nameColumn")}</th>
            <th className="px-3 py-2">{t("cityColumn")}</th>
            <th className="px-3 py-2">{t("statusColumn")}</th>
            <th className="px-3 py-2">
              <span className="sr-only">{t("actionsColumn")}</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100">
          {places.map((place) => (
            <tr key={place.placeId}>
              <td className="px-3 py-2">
                <button
                  type="button"
                  onClick={() => navigate(getPlaceSharePath(place.placeId))}
                  className="font-medium text-whart-text underline-offset-2 hover:underline"
                >
                  {place.name}
                </button>
              </td>
              <td className="px-3 py-2 text-neutral-600">
                {place.city ?? "—"}
              </td>
              <td className="px-3 py-2">
                <StatusBadge status={place.status} />
              </td>
              <td className="px-3 py-2 text-right">
                <Button
                  type="button"
                  variant="secondary"
                  className="px-2 py-1 text-xs"
                  onClick={() => onRemove(place.placeId)}
                >
                  {t("remove")}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SuggestedEventsTable({
  events,
  onRemove,
}: {
  events: ArticleSuggestedEvent[];
  onRemove: (eventId: string) => void;
}) {
  const t = useTranslations("articles.edit.suggestions.events");

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[40rem] table-fixed text-left text-sm">
        <colgroup>
          <col />
          <col className="w-32" />
          <col className="w-44" />
          <col className="w-32" />
          <col className="w-28" />
        </colgroup>
        <thead>
          <tr className="wireframe-border-b bg-neutral-50 text-xs font-medium uppercase tracking-wider text-neutral-500">
            <th className="px-3 py-2">{t("titleColumn")}</th>
            <th className="px-3 py-2">{t("typeColumn")}</th>
            <th className="px-3 py-2">{t("datesColumn")}</th>
            <th className="px-3 py-2">{t("statusColumn")}</th>
            <th className="px-3 py-2">
              <span className="sr-only">{t("actionsColumn")}</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100">
          {events.map((event) => (
            <tr key={event.eventId}>
              <td className="px-3 py-2">
                <Link
                  to={getEventSharePath(event.eventId)}
                  className="font-medium text-whart-text underline-offset-2 hover:underline"
                >
                  {event.title}
                </Link>
              </td>
              <td className="px-3 py-2 text-neutral-600">
                <EventCategoryLabel category={event.eventCategory} />
              </td>
              <td className="px-3 py-2 text-neutral-600">
                {formatDateRange(event.startDate, event.endDate)}
              </td>
              <td className="px-3 py-2">
                <StatusBadge status={event.status} />
              </td>
              <td className="px-3 py-2 text-right">
                <Button
                  type="button"
                  variant="secondary"
                  className="px-2 py-1 text-xs"
                  onClick={() => onRemove(event.eventId)}
                >
                  {t("remove")}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
